import React, { useState, useEffect } from 'react';
import { FormField } from './types';
import { useQuery } from '@tanstack/react-query';
import { api } from '../core/services/api';

interface MatrixFieldRendererProps {
  field: FormField;
  value: Array<{ rowValue: string; columnValue: string }>;
  onChange: (value: Array<{ rowValue: string; columnValue: string }>) => void;
}

interface MatrixConfig {
  rowField?: string;
  columnField?: string;
}

// Extend FormField interface to include matrix config
interface MatrixFormField extends FormField {
  matrixConfig?: MatrixConfig;
}

export function MatrixFieldRenderer({
  field,
  value = [],
  onChange,
}: MatrixFieldRendererProps) {
  const [uniqueRows, setUniqueRows] = useState<string[]>([]);
  const [uniqueColumns, setUniqueColumns] = useState<string[]>([]);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  
  const matrixField = field as MatrixFormField;

  const { data, isLoading } = useQuery({
    queryKey: ['matrix-data', field.dataSource],
    queryFn: async () => {
      if (!field.dataSource) return [];
      
      const requestBody = {
        system: field.dataSource.system,
        object_type: field.dataSource.objectType,
        action: field.dataSource.action,
        params: field.dataSource.params || {},
      };

      const response: any = await api.post('/api/crud/execute', requestBody);
      return response?.data?.result || response?.result || [];
    },
    enabled: !!field.dataSource,
  });

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const rows = [
        ...new Set(
          data.map((item: any) => item[matrixField.matrixConfig?.rowField || ''] as string)
        ),
      ].filter(Boolean);
      
      const cols = [
        ...new Set(
          data.map((item: any) => item[matrixField.matrixConfig?.columnField || ''] as string)
        ),
      ].filter(Boolean);
      
      setUniqueRows(rows);
      setUniqueColumns(cols);
    }
  }, [data, matrixField.matrixConfig]);

  useEffect(() => {
    const selected = new Set<string>(
      value.map((v: { rowValue: string; columnValue: string }) => `${v.rowValue}-${v.columnValue}`)
    );
    setSelectedCells(selected);
  }, [value]);

  const toggleCell = (rowValue: string, columnValue: string) => {
    const key = `${rowValue}-${columnValue}`;
    const newSelected = new Set(selectedCells);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelectedCells(newSelected);
    onChange(
      [...newSelected].map((key) => {
        const [row, col] = key.split('-');
        return { rowValue: row, columnValue: col };
      })
    );
  };

  if (isLoading) {
    return (
      <div style={{
        padding: '16px',
        textAlign: 'center',
        color: 'var(--main-text-secondary)',
        fontSize: '14px'
      }}>
        Loading matrix data...
      </div>
    );
  }

  if (!data || uniqueRows.length === 0 || uniqueColumns.length === 0) {
    return (
      <div style={{
        padding: '16px',
        background: 'var(--tag-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        color: 'var(--main-text-secondary)',
        fontSize: '14px'
      }}>
        No matrix data available
      </div>
    );
  }

  return (
    <div style={{ marginTop: '16px', overflowX: 'auto' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        background: 'var(--tag-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '4px'
      }}>
        <thead>
          <tr style={{ background: 'var(--disabled-bg)' }}>
            <th style={{
              padding: '12px',
              textAlign: 'left',
              fontSize: '12px',
              fontWeight: '600',
              color: 'var(--main-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderRight: '1px solid var(--border-color)'
            }}>
              {/* Empty corner cell */}
            </th>
            {uniqueColumns.map((col) => (
              <th
                key={col}
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--main-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderRight: '1px solid var(--border-color)'
                }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {uniqueRows.map((row, rowIndex) => (
            <tr 
              key={row}
              style={{ 
                borderBottom: rowIndex < uniqueRows.length - 1 ? '1px solid var(--border-color)' : 'none'
              }}
            >
              <td style={{
                padding: '12px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--main-text)',
                borderRight: '1px solid var(--border-color)'
              }}>
                {row}
              </td>
              {uniqueColumns.map((col) => (
                <td
                  key={`${row}-${col}`}
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    borderRight: '1px solid var(--border-color)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCells.has(`${row}-${col}`)}
                    onChange={() => toggleCell(row, col)}
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: 'var(--main-accent)',
                      cursor: 'pointer'
                    }}
                    aria-label={`Select ${row} - ${col}`}
                    title={`Select ${row} - ${col}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
